import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroVideo from "@/components/HeroVideo";
import EventCard from "@/components/EventCard";
import CountUp from "@/components/CountUp";
import { events } from "@/data/content";
import { useLang } from "@/context/LanguageContext";

export default function Home() {
  const { t, lang } = useLang();

  // Dynamic month labels (auto-update — always shows current + next month)
  const now = new Date();
  const currentMonthIdx = now.getMonth();
  const currentYear = now.getFullYear();
  const nextMonthDate = new Date(currentYear, currentMonthIdx + 1, 1);
  const nextMonthIdx = nextMonthDate.getMonth();
  const nextYear = nextMonthDate.getFullYear();
  const currentMonthLabel = t.months[currentMonthIdx];
  const nextMonthLabel = t.months[nextMonthIdx];

  // Helper: events for a given month/year (skip sold-out)
  const eventsIn = (mIdx, y) =>
    events.filter((e) => {
      if (e.soldOut) return false;
      const d = new Date(e.date);
      return d.getMonth() === mIdx && d.getFullYear() === y;
    });

  const currentMonthEvents = eventsIn(currentMonthIdx, currentYear);
  const nextMonthEvents = eventsIn(nextMonthIdx, nextYear);

  // Fallback: if both months are empty, show next 6 upcoming
  const fallbackUpcoming =
    currentMonthEvents.length === 0 && nextMonthEvents.length === 0
      ? events.filter((e) => !e.soldOut).slice(0, 6)
      : null;

  return (
    <div className="min-h-screen bg-[var(--sv-bg)] text-white">
      <Navbar />

      {/* ============== HERO ============== */}
      <section
        className="relative h-screen w-full overflow-hidden"
        data-testid="hero-section"
      >
        <HeroVideo />

        {/* Side vertical labels */}
        <div className="hidden lg:flex absolute left-8 top-1/2 -translate-y-1/2 z-20">
          <span className="vertical-label text-white/40 font-display font-medium">
            From Seoul · To your city
          </span>
        </div>
        <div className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 z-20">
          <span className="vertical-label text-white/40 font-display font-medium">
            CZ · SK · 2025–26
          </span>
        </div>

        {/* Centered tagline */}
        <div className="absolute inset-0 z-20 flex items-start md:items-center justify-center px-6 pt-[18vh] md:pt-0">
          <div className="text-center fade-up max-w-3xl">
            <h1
              className="font-display font-medium tracking-[0.06em] text-2xl sm:text-3xl md:text-4xl text-white/95"
              data-testid="hero-heading"
            >
              <span className="neon-text-purple font-bold">서울</span>
              <span className="font-light italic"> Vibes </span>
              <span className="uppercase tracking-[0.18em] text-base sm:text-lg md:text-xl text-white/85">
                in Europe
              </span>
            </h1>

            {/* CTA button — directly below the tagline */}
            <div className="mt-6 md:mt-7 flex items-center justify-center">
              <Link
                to="/events"
                className="sv-btn sv-btn-primary"
                data-testid="hero-buy-tickets"
              >
                {t.hero.buyTickets} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Compact stats below CTA */}
            <div
              className="mt-6 md:mt-7 flex flex-wrap items-center justify-center gap-x-5 md:gap-x-7 gap-y-2"
              data-testid="stats-strip"
            >
              <div className="flex items-baseline gap-2" data-testid="stat-parties">
                <span className="font-display font-bold text-sm md:text-base tracking-tight text-white">
                  <CountUp end={100} suffix="+" testid="stat-parties-value" />
                </span>
                <span className="font-display uppercase tracking-[0.25em] text-[9px] md:text-[10px] text-white/55">
                  {t.stats.parties}
                </span>
              </div>
              <span className="hidden md:inline-block w-px h-3 bg-white/15" />
              <div className="flex items-baseline gap-2" data-testid="stat-attendees">
                <span className="font-display font-bold text-sm md:text-base tracking-tight text-white">
                  <CountUp end={5000} suffix="+" testid="stat-attendees-value" />
                </span>
                <span className="font-display uppercase tracking-[0.25em] text-[9px] md:text-[10px] text-white/55">
                  {t.stats.attendees}
                </span>
              </div>
              <span className="hidden md:inline-block w-px h-3 bg-white/15" />
              <div className="flex items-baseline gap-2" data-testid="stat-europe">
                <span className="font-display font-bold text-sm md:text-base tracking-tight neon-text-purple">
                  <CountUp end={12} testid="stat-cities-value" />
                </span>
                <span className="font-display uppercase tracking-[0.25em] text-[9px] md:text-[10px] text-white/55">
                  {lang === "en" ? "cities" : "měst"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator removed — content peeks above the fold now */}
      </section>

      {/* ============== UPCOMING EVENTS ============== */}
      <section className="relative pt-0 pb-24 md:pb-32 -mt-72 md:-mt-96 z-30" data-testid="upcoming-section">
        <div className="container-sv">
          {/* Heading + see all — minimal */}
          <div className="flex items-end justify-between gap-6 mb-8">
            <h2
              className="font-display font-bold uppercase text-lg md:text-2xl tracking-[0.05em] leading-none text-white"
              data-testid="upcoming-heading"
            >
              {t.upcomingTitle}
            </h2>
            <Link
              to="/events"
              className="font-display uppercase tracking-[0.25em] text-[10px] md:text-[11px] text-white/60 hover:text-white transition-colors flex items-center gap-1.5"
              data-testid="upcoming-see-all"
            >
              {t.hero.cta} <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {/* Two month blocks — always shown vertically: current month, then next month */}
          <div className="space-y-14">
            <MonthBlock
              monthLabel={currentMonthLabel}
              year={currentYear}
              items={currentMonthEvents}
              lang={lang}
              testid="month-block-current"
            />
            <MonthBlock
              monthLabel={nextMonthLabel}
              year={nextYear}
              items={nextMonthEvents}
              lang={lang}
              testid="month-block-next"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function MonthBlock({ monthLabel, year, items, lang, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-3 mb-5">
        <span className="font-display uppercase tracking-[0.3em] text-[11px] text-white/80">
          {monthLabel} <span className="text-white/40">/ {year}</span>
        </span>
        <span className="flex-1 h-px bg-white/10" />
        <span className="font-display text-[10px] uppercase tracking-[0.25em] text-white/40">
          {items.length} {lang === "en" ? "nights" : "akcí"}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-white/45 font-body text-sm">
          {lang === "en"
            ? "Lineup dropping soon."
            : "Lineup brzy."}
        </p>
      ) : (
        <div className="flex gap-4 overflow-x-auto no-scrollbar -mx-6 px-6 pb-2 snap-x snap-mandatory sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 md:gap-7 sm:overflow-visible sm:mx-0 sm:px-0 sm:snap-none">
          {items.slice(0, 4).map((e) => (
            <div
              key={e.id}
              className="snap-start flex-shrink-0 w-[230px] sm:w-auto"
            >
              <EventCard event={e} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
